import { useEffect, useRef } from "react";
import type { DisplayItem, DisplayGroup } from "../lib/menu.js";
import { groupNumber } from "../lib/menu.js";
import { formatMoney } from "../lib/money.js";
import type { Cart } from "../lib/cart.js";
import "./Menu.css";

interface RowProps {
  item: DisplayItem;
  quantity: number;
  cart: Cart;
  paused: boolean;
}

/**
 * One menu row: what it is on the left, what it costs and how to order it on
 * the right.
 *
 * The Add button is replaced in place by the stepper, so the control does not
 * move when the first one is added. That swap destroys the button the customer
 * just pressed, which would drop focus to the body, so focus is handed to the
 * stepper's "+" — but only when this component caused the swap. Without that
 * guard every row already in the cart would fight for focus on mount.
 */
function Row({ item, quantity, cart, paused }: RowProps) {
  const inCart = quantity > 0;
  const justAdded = useRef(false);
  const plusRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (justAdded.current && inCart) {
      justAdded.current = false;
      plusRef.current?.focus();
    }
  }, [inCart]);

  const addOne = () => {
    cart.add({ menuItemId: item.id, name: item.name, priceCents: item.priceCents });
  };

  return (
    <li className="row">
      <div className="row__ident">
        <h3 className="row__name">{item.name}</h3>
        {item.description !== "" && <p className="row__desc">{item.description}</p>}
      </div>

      <p className="row__price">{formatMoney(item.priceCents)}</p>

      <div className="row__control">
        {inCart ? (
          <div className="stepper" role="group" aria-label={`Quantity, ${item.name}`}>
            <button
              type="button"
              className="stepper__step"
              disabled={paused}
              onClick={() => cart.decrement(item.id)}
              aria-label={`Remove one ${item.name}`}
            >
              &minus;
            </button>
            {/* The count is the group's value, announced by the buttons'
                labels; marking it up as text keeps it out of the tab order. */}
            <span className="stepper__count">{quantity}</span>
            <button
              type="button"
              ref={plusRef}
              className="stepper__step"
              disabled={paused}
              onClick={addOne}
              aria-label={`Add another ${item.name}`}
            >
              +
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="row__add"
            disabled={paused}
            onClick={() => {
              justAdded.current = true;
              addOne();
            }}
          >
            Add<span className="u-sr-only"> {item.name}</span>
          </button>
        )}
      </div>
    </li>
  );
}

interface MenuProps {
  groups: readonly DisplayGroup[];
  cart: Cart;
  paused: boolean;
}

export default function Menu({ groups, cart, paused }: MenuProps) {
  return (
    <div className="menu">
      {groups.map((group, index) => (
        <section className="group" key={group.id} aria-labelledby={`group-${group.id}`}>
          {/* The rule: name, a hairline that takes up the slack, and the
              number. The number is legitimate here — a menu is ordered. */}
          <div className="group__rule">
            <h2 className="group__name" id={`group-${group.id}`}>
              {group.name}
            </h2>
            <span className="group__line" aria-hidden="true" />
            <span className="group__no" aria-hidden="true">
              {groupNumber(index)}
            </span>
          </div>

          <ul className="group__items">
            {group.items.map((item) => (
              <Row
                key={item.id}
                item={item}
                quantity={cart.quantityOf(item.id)}
                cart={cart}
                paused={paused}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
